import * as React from 'react';
import { Check, ChevronDown, Loader2, Search, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/utils';

type SelectAllMode = 'loaded' | 'allMatching';
type SelectedDisplayMode = 'count' | 'tags' | 'badge' | 'none';

export interface MultiSelectTableColumn<TOption> {
  id: string;
  title: React.ReactNode;
  accessor?: keyof TOption | ((item: TOption) => React.ReactNode);
  className?: string;
  headerClassName?: string;
  cellClassName?: string;
}

export interface MultiSelectDropdownProps<TOption> {
  label?: React.ReactNode;
  placeholder?: string;
  options: TOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[], selectedOptions: TOption[]) => void;
  isMulti?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  showSelectAll?: boolean;
  selectAllMode?: SelectAllMode;
  selectAllLabel?: React.ReactNode;
  selectAllDescription?: React.ReactNode;
  onSelectAllMatching?: (searchTerm: string) => void | Promise<void>;
  showSelectedCount?: boolean;
  showSelectedTags?: boolean;
  showBadgeCount?: boolean;
  selectedDisplayMode?: SelectedDisplayMode;
  showCheckboxes?: boolean;
  showTicks?: boolean;
  lazyLoad?: boolean;
  pageSize?: number;
  hasMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: (searchTerm: string) => void | Promise<void>;
  onSearch?: (searchTerm: string) => void | Promise<void>;
  renderOption?: (option: TOption, isSelected: boolean) => React.ReactNode;
  getOptionLabel?: (option: TOption) => string;
  getOptionValue?: (option: TOption) => string;
  tableEnabled?: boolean;
  tableColumns?: Array<MultiSelectTableColumn<TOption>>;
  tableData?: TOption[];
  renderTableCell?: (item: TOption, column: MultiSelectTableColumn<TOption>) => React.ReactNode;
  onDeleteSelectedItem?: (item: TOption, value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  minSelected?: number;
  maxSelected?: number;
  validateOnRender?: boolean;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  tableClassName?: string;
  name?: string;
  id?: string;
}

const DEFAULT_PAGE_SIZE = 20;

const defaultGetOptionLabel = <TOption,>(option: TOption): string => {
  if (option && typeof option === 'object') {
    const record = option as Record<string, unknown>;
    const label = record.label ?? record.name ?? record.title ?? record.value ?? record.id;
    if (label !== undefined && label !== null) return String(label);
  }

  return String(option ?? '');
};

const defaultGetOptionValue = <TOption,>(option: TOption): string => {
  if (option && typeof option === 'object') {
    const record = option as Record<string, unknown>;
    const value = record.value ?? record.id ?? record.key ?? record.label ?? record.name;
    if (value !== undefined && value !== null) return String(value);
  }

  return String(option ?? '');
};

const getColumnValue = <TOption,>(
  item: TOption,
  column: MultiSelectTableColumn<TOption>,
): React.ReactNode => {
  if (!column.accessor) return null;
  if (typeof column.accessor === 'function') return column.accessor(item);
  const value = item[column.accessor];
  if (value === undefined || value === null || value === '') return '-';
  return value as React.ReactNode;
};

export function MultiSelectDropdown<TOption>({
  label,
  placeholder = 'Select options',
  options,
  selectedValues,
  onChange,
  isMulti = true,
  searchable = true,
  searchPlaceholder = 'Search',
  showSelectAll = false,
  selectAllMode = 'loaded',
  selectAllLabel = 'Select all',
  selectAllDescription,
  onSelectAllMatching,
  showSelectedCount = false,
  showSelectedTags = false,
  showBadgeCount = false,
  selectedDisplayMode,
  showCheckboxes = true,
  showTicks = false,
  lazyLoad = false,
  pageSize = DEFAULT_PAGE_SIZE,
  hasMore = false,
  isLoading = false,
  onLoadMore,
  onSearch,
  renderOption,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  tableEnabled = false,
  tableColumns = [],
  tableData,
  renderTableCell,
  onDeleteSelectedItem,
  error,
  required = false,
  disabled = false,
  minSelected,
  maxSelected,
  validateOnRender = false,
  className,
  triggerClassName,
  dropdownClassName,
  tableClassName,
  name,
  id,
}: MultiSelectDropdownProps<TOption>) {
  const generatedId = React.useId();
  const componentId = id ?? generatedId;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const selectAllRef = React.useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(pageSize);
  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);
  const usesRemoteLazyLoading = lazyLoad && Boolean(onSearch || onLoadMore);

  const locallyFilteredOptions = React.useMemo(() => {
    if (lazyLoad && onSearch) return options;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter((option) => (
      getOptionLabel(option).toLowerCase().includes(normalizedSearch)
    ));
  }, [getOptionLabel, lazyLoad, onSearch, options, searchTerm]);

  const visibleOptions = React.useMemo(() => {
    if (!lazyLoad || usesRemoteLazyLoading) return locallyFilteredOptions;
    return locallyFilteredOptions.slice(0, visibleCount);
  }, [lazyLoad, locallyFilteredOptions, usesRemoteLazyLoading, visibleCount]);

  const selectedOptions = React.useMemo(() => (
    options.filter((option) => selectedSet.has(getOptionValue(option)))
  ), [getOptionValue, options, selectedSet]);

  const tableRows = tableData ?? selectedOptions;
  const loadedValues = React.useMemo(() => (
    visibleOptions.map((option) => getOptionValue(option))
  ), [getOptionValue, visibleOptions]);
  const selectedLoadedCount = loadedValues.filter((value) => selectedSet.has(value)).length;
  const areAllLoadedSelected = loadedValues.length > 0 && selectedLoadedCount === loadedValues.length;
  const areSomeLoadedSelected = selectedLoadedCount > 0 && !areAllLoadedSelected;

  React.useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = areSomeLoadedSelected;
  }, [areSomeLoadedSelected]);

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    setVisibleCount(pageSize);
  }, [pageSize, searchTerm]);

  const computedError = React.useMemo(() => {
    if (error) return error;
    if (!validateOnRender) return '';
    if (required && selectedValues.length === 0) return 'Select at least one option.';
    if (minSelected && selectedValues.length < minSelected) return `Select at least ${minSelected} option${minSelected === 1 ? '' : 's'}.`;
    if (maxSelected && selectedValues.length > maxSelected) return `Select no more than ${maxSelected} option${maxSelected === 1 ? '' : 's'}.`;
    return '';
  }, [error, maxSelected, minSelected, required, selectedValues.length, validateOnRender]);

  const commitSelection = (nextValues: string[]) => {
    const normalizedValues = Array.from(new Set(nextValues));
    const nextSelectedOptions = options.filter((option) => normalizedValues.includes(getOptionValue(option)));
    onChange(normalizedValues, nextSelectedOptions);
  };

  const toggleOption = (option: TOption) => {
    if (disabled) return;

    const value = getOptionValue(option);
    const isSelected = selectedSet.has(value);

    if (!isMulti) {
      commitSelection(isSelected ? [] : [value]);
      setIsOpen(false);
      return;
    }

    if (isSelected) {
      commitSelection(selectedValues.filter((selectedValue) => selectedValue !== value));
      return;
    }

    if (maxSelected && selectedValues.length >= maxSelected) return;
    commitSelection([...selectedValues, value]);
  };

  const toggleSelectAll = async () => {
    if (disabled || loadedValues.length === 0) return;

    if (lazyLoad && selectAllMode === 'allMatching' && onSelectAllMatching) {
      await onSelectAllMatching(searchTerm);
      return;
    }

    if (areAllLoadedSelected) {
      commitSelection(selectedValues.filter((value) => !loadedValues.includes(value)));
      return;
    }

    const availableValues = maxSelected
      ? loadedValues.filter((value) => !selectedSet.has(value)).slice(0, Math.max(maxSelected - selectedValues.length, 0))
      : loadedValues.filter((value) => !selectedSet.has(value));
    commitSelection([...selectedValues, ...availableValues]);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (lazyLoad && onSearch) {
      void onSearch(value);
    }
  };

  const clearSearch = () => {
    handleSearchChange('');
  };

  const handleDropdownScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!lazyLoad) return;

    const target = event.currentTarget;
    const scrollOffset = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (scrollOffset > 24 || isLoading) return;

    if (hasMore && onLoadMore) {
      void onLoadMore(searchTerm);
      return;
    }

    if (visibleCount < locallyFilteredOptions.length) {
      setVisibleCount((current) => Math.min(current + pageSize, locallyFilteredOptions.length));
    }
  };

  const deleteSelectedItem = (item: TOption) => {
    const value = getOptionValue(item);
    commitSelection(selectedValues.filter((selectedValue) => selectedValue !== value));
    onDeleteSelectedItem?.(item, value);
  };

  const renderSelectedDisplay = () => {
    const displayMode = selectedDisplayMode ?? (
      showSelectedTags ? 'tags' : showBadgeCount ? 'badge' : showSelectedCount ? 'count' : 'none'
    );

    if (selectedValues.length === 0 || displayMode === 'none') {
      return <span className="truncate text-neutral-500">{placeholder}</span>;
    }

    if (displayMode === 'tags') {
      return (
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          {selectedOptions.slice(0, 3).map((option) => {
            const value = getOptionValue(option);
            return (
              <span
                key={value}
                className="inline-flex max-w-[10rem] items-center gap-1 rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-800"
              >
                <span className="truncate">{getOptionLabel(option)}</span>
                <X className="h-3 w-3" aria-hidden="true" />
              </span>
            );
          })}
          {selectedValues.length > 3 && (
            <span className="text-xs font-medium text-neutral-600">+{selectedValues.length - 3}</span>
          )}
        </span>
      );
    }

    return (
      <>
        <span className="truncate text-neutral-500">{placeholder}</span>
        <span
          className={cn(
            'ml-auto flex-shrink-0 text-xs font-semibold text-neutral-700',
            displayMode === 'badge' && 'rounded-full bg-primary-50 px-2 py-0.5 text-primary-700',
          )}
        >
          {selectedValues.length} selected
        </span>
      </>
    );
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {label && (
        <label htmlFor={componentId} className="mb-2 block text-sm font-semibold text-neutral-950">
          {label} {required && <span className="text-error-700">*</span>}
        </label>
      )}

      <input type="hidden" name={name} value={selectedValues.join(',')} />

      <button
        id={componentId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={Boolean(computedError)}
        aria-required={required}
        onClick={() => {
          if (!disabled) setIsOpen((current) => !current);
        }}
        className={cn(
          'flex min-h-[42px] w-full items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 text-left text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-500 focus:border-primary-500',
          isOpen && 'border-primary-500',
          disabled && 'cursor-not-allowed bg-neutral-100 text-neutral-400',
          computedError && 'border-error-300',
          triggerClassName,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {renderSelectedDisplay()}
        </span>
        <ChevronDown className={cn('h-4 w-4 flex-shrink-0 text-neutral-600 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute left-0 right-0 z-[140] mt-1 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg',
            dropdownClassName,
          )}
        >
          {(searchable || showSelectAll) && (
            <div className="space-y-2 border-b border-neutral-100 px-3 py-2">
              {searchable && (
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
                  <input
                    value={searchTerm}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    placeholder={searchPlaceholder}
                    className="h-8 w-full rounded-md border border-neutral-200 bg-white pl-8 pr-8 text-xs text-neutral-900 outline-none placeholder:text-neutral-500 focus:border-primary-500"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 flex h-[1.125rem] w-[1.125rem] -translate-y-1/2 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
                      title="Clear search"
                      aria-label="Clear search"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </label>
              )}

              {showSelectAll && (
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => void toggleSelectAll()}
                    disabled={loadedValues.length === 0}
                    className="inline-flex min-w-0 items-center gap-2 rounded-md py-1 pr-2 text-left text-xs font-semibold text-neutral-800 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent"
                  >
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={areAllLoadedSelected}
                      readOnly
                      disabled={loadedValues.length === 0}
                      className="h-3.5 w-3.5 rounded border-neutral-300 accent-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                    <span className="truncate">{selectAllLabel}</span>
                  </button>

                  {selectAllDescription && (
                    <span className="flex-shrink-0 text-xs font-medium text-neutral-500">
                      {selectAllDescription}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="max-h-64 overflow-y-auto py-1" role="listbox" aria-multiselectable={isMulti} onScroll={handleDropdownScroll}>
            {showSelectAll && !searchable && (
              <button
                type="button"
                onClick={() => void toggleSelectAll()}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm font-medium text-neutral-800 hover:bg-primary-50"
              >
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={areAllLoadedSelected}
                  readOnly
                  disabled={loadedValues.length === 0}
                  className="h-4 w-4 rounded border-neutral-300 accent-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{selectAllLabel}</span>
                  {selectAllDescription && (
                    <span className="block truncate text-xs font-normal text-neutral-500">
                      {selectAllDescription}
                    </span>
                  )}
                </span>
              </button>
            )}

            {visibleOptions.map((option) => {
              const value = getOptionValue(option);
              const isSelected = selectedSet.has(value);

              return (
                <button
                  key={value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggleOption(option)}
                  className={cn(
                    'flex w-full items-center gap-3 border-b border-neutral-100 px-3 py-2.5 text-left text-sm text-neutral-800 last:border-b-0 hover:bg-primary-50',
                    isSelected && 'bg-primary-50/60',
                  )}
                >
                  {showCheckboxes && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="h-4 w-4 rounded border-neutral-300 accent-primary-500"
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {renderOption ? renderOption(option, isSelected) : getOptionLabel(option)}
                  </span>
                  {showTicks && isSelected && (
                    <Check className="h-4 w-4 flex-shrink-0 text-primary-600" />
                  )}
                </button>
              );
            })}

            {!isLoading && visibleOptions.length === 0 && (
              <div className="px-3 py-6 text-center text-sm font-medium text-neutral-600">
                No results found.
              </div>
            )}

            {isLoading && (
              <div className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-neutral-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            )}
          </div>
        </div>
      )}

      <p className="mt-1 min-h-[1rem] text-xs text-error-700">{computedError}</p>

      {tableEnabled && tableColumns.length > 0 && (
        <div className={cn('mt-3 overflow-hidden rounded-md border border-neutral-200 bg-white', tableClassName)}>
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-neutral-50 text-[0.7rem] font-semibold text-neutral-700">
              <tr className="border-b border-neutral-200">
                {tableColumns.map((column) => (
                  <th key={column.id} className={cn('px-3 py-2', column.headerClassName, column.className)}>
                    {column.title}
                  </th>
                ))}
                {onDeleteSelectedItem && (
                  <th className="w-16 px-3 py-2 text-center">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="text-[0.72rem] text-neutral-800">
              {tableRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableColumns.length + (onDeleteSelectedItem ? 1 : 0)}
                    className="px-3 py-6 text-center text-sm font-medium text-neutral-600"
                  >
                    No selected items.
                  </td>
                </tr>
              ) : (
                tableRows.map((item) => {
                  const value = getOptionValue(item);
                  return (
                    <tr key={value} className="border-b border-neutral-100 last:border-b-0 hover:bg-primary-50/50">
                      {tableColumns.map((column) => (
                        <td key={`${value}-${column.id}`} className={cn('px-3 py-2 align-top', column.cellClassName, column.className)}>
                          {renderTableCell ? renderTableCell(item, column) : getColumnValue(item, column)}
                        </td>
                      ))}
                      {onDeleteSelectedItem && (
                        <td className="px-3 py-2 text-center align-top">
                          <button
                            type="button"
                            aria-label={`Remove ${getOptionLabel(item)}`}
                            onClick={() => deleteSelectedItem(item)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-error-700 transition-colors hover:bg-error-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
